/**
 * Client-side image compression utility using the Canvas API.
 * Resizes and converts images to JPEG before upload to reduce file size.
 * No external dependencies.
 */

// File types that should NOT be compressed
const SKIP_TYPES = new Set([
  'image/gif',       // Animated — loses animation on canvas
  'image/svg+xml',   // Vector — canvas has CORS/security issues
])

// HEIC types — attempt compression (works in Safari), fall back to original if it fails
const HEIC_TYPES = new Set([
  'image/heic',
  'image/heif',
])

const DEFAULT_OPTIONS = {
  maxWidth: 1600,
  maxHeight: 1200,
  quality: 0.8,
  skipBelowBytes: 200 * 1024, // 200KB — small files don't benefit from compression
}

/**
 * Compresses an image file and returns a Blob.
 * Use this for S3 upload flows (signed URL + PUT).
 *
 * - Resizes to fit within maxWidth/maxHeight (uniform scale, no stretching)
 * - Converts to JPEG (or keeps PNG for transparency)
 * - HEIC: attempts compression (works in Safari), falls back to original if browser can't decode
 * - Skips compression for small files, non-images, GIFs, SVGs
 *
 * @param {File} file - The image file to compress
 * @param {Object} [options]
 * @param {number} [options.maxWidth=1600] - Max width in pixels
 * @param {number} [options.maxHeight=1200] - Max height in pixels
 * @param {number} [options.quality=0.8] - JPEG quality 0-1
 * @param {number} [options.skipBelowBytes=204800] - Skip files smaller than this
 * @returns {Promise<Blob>} Compressed image blob (or original file if skipped)
 */
export function compressImageToBlob(file, options = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options }

  // Skip non-image files
  if (!file || !file.type || !file.type.startsWith('image/')) {
    return Promise.resolve(file)
  }

  // Skip types that can't or shouldn't be compressed via canvas
  if (SKIP_TYPES.has(file.type)) {
    return Promise.resolve(file)
  }

  const isHeic = HEIC_TYPES.has(file.type)

  // Skip already-small files — compression may make them larger (but always try HEIC)
  if (!isHeic && file.size < opts.skipBelowBytes) {
    return Promise.resolve(file)
  }

  const compressPromise = new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = () => reject(new Error('Failed to read image file'))

    reader.onload = (e) => {
      const img = new Image()

      img.onerror = () => reject(new Error('Failed to load image for compression'))

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          let width = img.width
          let height = img.height

          // Uniform scale — same ratio for both dimensions, no stretching
          if (width > opts.maxWidth || height > opts.maxHeight) {
            const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }

          canvas.width = width
          canvas.height = height

          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)

          // Always convert to JPEG — smaller file size, transparency not needed for uploads
          const outputType = 'image/jpeg'
          const qualityParam = opts.quality

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob)
              } else {
                reject(new Error('Canvas toBlob returned null'))
              }
            },
            outputType,
            qualityParam
          )
        } catch (err) {
          reject(err)
        }
      }

      img.src = e.target.result
    }

    reader.readAsDataURL(file)
  })

  // HEIC: attempt compression but fall back to original if browser can't decode
  if (isHeic) {
    return compressPromise.catch(() => file)
  }

  return compressPromise
}

/**
 * Compresses an image and returns a base64 string (without MIME prefix).
 * For backward compatibility with components that upload base64-encoded images.
 *
 * @param {File} file - The image file to compress
 * @param {Object} [options] - Same as compressImageToBlob, defaults to maxWidth 1000
 * @returns {Promise<string>} Base64-encoded compressed image (no MIME prefix)
 */
export async function compressImageToBase64(file, options = {}) {
  const opts = { maxWidth: 1000, maxHeight: 1000, ...options }
  const blob = await compressImageToBlob(file, opts)

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Failed to convert blob to base64'))
    reader.onload = () => {
      // Remove the "data:image/jpeg;base64," prefix
      const base64 = reader.result.split(',')[1]
      resolve(base64)
    }
    reader.readAsDataURL(blob)
  })
}
